import React, { useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { FilterSortProvider } from './lib/FilterSortProvider';
import { NotificationProvider } from './lib/NotificationProvider';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { UserDataProvider, useUserData } from './contexts/UserDataContext';
import { QueryClientProvider } from '@tanstack/react-query';
import { queryClient } from './lib/queryClient';
import Sidebar from './components/Sidebar';
import Dashboard from './components/Dashboard';
import LineItemForm from './components/LineItemForm';
import ShipmentForm from './components/ShipmentForm';
import UserManagement from './components/UserManagement';
import Reports from './components/Reports';
import Notification from './components/Notification';
import UserAuth from './components/UserAuth';
import { useLineItems } from './hooks/useLineItems';
import './App.css';

// Protected Route component to check authentication
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated } = useAuth();
  const location = useLocation();

  if (!isAuthenticated) {
    // Redirect to login page, but save the current location
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  return children;
};

// Main content component that depends on user data
const MainContent = () => {
  const [isLoading, setIsLoading] = useState(true);
  
  // Get user data from context
  const { userData, isLoading: userDataLoading } = useUserData();

  // // Add console logging for userData in MainContent
  // useEffect(() => {
  //   console.log('MainContent: userData state:', {
  //     userData,
  //     isLoading: userDataLoading,
  //     hasRole: userData?.role,
  //     role: userData?.role,
  //     canAccess: userData?.role !== "Employee"
  //   });
  // }, [userData, userDataLoading]);

  // useLineItems' queryKey includes userData?.role, so React Query already
  // refetches automatically when the role changes. No manual invalidate needed.
  const { data: lineItems, isLoading: lineItemsLoading } = useLineItems('', userData);

  // Set loading state based on user data and line items loading
  useEffect(() => {
    setIsLoading(userDataLoading || lineItemsLoading);
  }, [userDataLoading, lineItemsLoading]);

  return (
    <div className="flex w-full">
      <Sidebar />
      <main className="flex-1 overflow-auto">
        <Routes>
          <Route
            path=""
            element={<Dashboard tableData={lineItems || []} isLoading={isLoading} />}
          />
          <Route
            path="line-item"
            element={<LineItemForm lineItems={lineItems || []} />}
          />
          <Route
            path="shipment-details/:id"
            element={<ShipmentForm />}
          />
          <Route
            path="create-shipment"
            element={<ShipmentForm isCreate={true} />}
          />
          <Route
            path="analytics"
            element={<div className="p-4">Analytics Coming Soon</div>}
          />
          <Route
            path="user-management"
            element={<UserManagement />}
          />
          <Route
            path="reports"
            element={<Reports />}
          />
          <Route path="*" element={<Navigate to="" replace />} />
        </Routes>
      </main>
    </div>
  );
};

function App() {
  // Add console log to show component tree initialization
  console.log('App: Initializing component tree with context providers');
  
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <UserDataProvider>
          <FilterSortProvider>
            <Router>
              <NotificationProvider>
                <div className="flex h-screen bg-gray-50">
                  <Routes>
                    {/* Authentication Routes - Public */}
                    <Route path="/login" element={<UserAuth />} />
                    <Route path="/register" element={<UserAuth />} />
                    <Route path="/reset-password" element={<UserAuth />} />
                    
                    {/* Protected Routes */}
                    <Route
                      path="/*"
                      element={
                        <ProtectedRoute>
                          <MainContent />
                        </ProtectedRoute>
                      }
                    />
                  </Routes>
                  <Notification />
                </div>
              </NotificationProvider>
            </Router>
          </FilterSortProvider>
        </UserDataProvider>
      </AuthProvider>
    </QueryClientProvider>
  );
}

export default App;
